import { Input } from 'antd';
import type { ComponentProps, ReactNode } from 'react';
import type { Control, FieldPath, FieldValues } from 'react-hook-form';

import { FormField } from '../form-field';

import styles from './text-field.module.scss';

type AntdInputProps = Omit<
  ComponentProps<typeof Input>,
  'value' | 'onChange' | 'defaultValue' | 'prefix' | 'suffix' | 'size'
>;

export type TextFieldProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> = AntdInputProps & {
  name: TName;
  control: Control<TFieldValues>;
  label?: string;
  help?: string;
  required?: boolean;
  prefix?: ReactNode;
  suffix?: ReactNode;
  /** Figma heights: default = 40px, tall = 46px. */
  fieldSize?: 'default' | 'tall';
  /** Rewrites the raw input string before it reaches RHF — for a field that must **reject characters
   *  as they are typed** rather than validate afterwards (e.g. a digits-only serial number). Pair it
   *  with `maxLength` for a length cap, which the DOM enforces natively including on paste. Defaults
   *  to identity, so no existing consumer changes. */
  transform?: (raw: string) => string;
  /** Opt-in: refuse pasted and dropped content, so the value can only be typed (110944 — a
   *  Confirm Password field pasted from the field above confirms nothing). Defaults to off, so
   *  no existing consumer changes. */
  disablePaste?: boolean;
};

function affix(node: ReactNode) {
  return typeof node === 'string' ? <span className={styles.affixText}>{node}</span> : node;
}

export function TextField<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>>({
  name,
  control,
  label,
  help,
  required,
  prefix,
  suffix,
  fieldSize = 'default',
  // Destructured, not left in `...rest` — spreading it would reach the DOM input as an unknown
  // attribute.
  transform,
  disablePaste,
  type,
  className,
  autoFocus,
  status: externalStatus,
  ...rest
}: TextFieldProps<TFieldValues, TName>) {
  const InputComponent = type === 'password' ? Input.Password : Input;
  // `paste` covers the keyboard shortcut and the context menu alike; `drop` is the remaining
  // route by which text can arrive without being typed.
  const block = disablePaste
    ? (e: { preventDefault: () => void }) => e.preventDefault()
    : undefined;
  return (
    <FormField name={name} control={control} label={label} help={help} required={required}>
      {({ field, fieldState }) => (
        <InputComponent
          id={name}
          autoFocus={autoFocus}
          size={fieldSize === 'tall' ? 'large' : 'middle'}
          prefix={affix(prefix)}
          suffix={affix(suffix)}
          type={type === 'password' ? undefined : type}
          className={[styles.input, className].filter(Boolean).join(' ')}
          {...rest}
          // After {...rest} and field-error first, so an external status prop can't
          // clobber the validation error (and vice versa).
          status={fieldState.error ? 'error' : externalStatus}
          value={field.value ?? ''}
          onChange={transform ? (e) => field.onChange(transform(e.target.value)) : field.onChange}
          onBlur={field.onBlur}
          onPaste={block}
          onDrop={block}
          ref={field.ref}
        />
      )}
    </FormField>
  );
}
